import { InfiniteData, QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys } from '../../api/queryKeys';
import type {
  NotificationEntity,
  NotificationsUnreadResponse,
} from '../../types/notification.types';

/**
 * Everything that writes the bell's caches by hand - the socket, and the two
 * mark actions - so the shape of a cached list is known in one place.
 */

/**
 * Retry policy for the bell's queries: the app default (one retry on a 5xx or
 * a network error, none on a 4xx) plus up to two retries on a 401.
 *
 * Seen on dev: a page loaded with an expired access token fires the count and
 * the list together, the interceptor refreshes once for both, and one of them
 * can still come back 401. The app default never retries a 4xx, so the badge
 * stayed empty for the rest of the visit while the list beside it was fine.
 * A 401 that survived the interceptor is a race, not a verdict - asking again
 * a moment later answers it.
 */
export const retryNotificationsQuery = (
  failureCount: number,
  error: unknown
) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401) return failureCount < 2;
    if (status !== undefined && status >= 400 && status < 500) return false;
  }
  return failureCount < 1;
};

/** One page size for the dropdown and the full page: they share the cache. */
export const NOTIFICATIONS_PAGE_SIZE = 20;

export type NotificationsPage = {
  items: NotificationEntity[];
  nextPage: number | undefined;
};

type NotificationsList = InfiniteData<NotificationsPage, number>;

export const getUnreadCount = (queryClient: QueryClient, userId: number) =>
  queryClient.getQueryData<NotificationsUnreadResponse>(
    queryKeys.notifications.unreadCount(userId)
  )?.unread;

export const setUnreadCount = (
  queryClient: QueryClient,
  userId: number,
  unread: number
) =>
  queryClient.setQueryData<NotificationsUnreadResponse>(
    queryKeys.notifications.unreadCount(userId),
    { unread: Math.max(0, unread) }
  );

/** Apply `update` to every cached list - the full one and the unread one. */
const updateLists = (
  queryClient: QueryClient,
  userId: number,
  update: (list: NotificationsList) => NotificationsList
) =>
  queryClient.setQueriesData<NotificationsList>(
    { queryKey: queryKeys.notifications.listAll(userId) },
    (list: NotificationsList | undefined) => (list ? update(list) : list)
  );

/**
 * A new one at the top of every list that is loaded.
 *
 * A list nobody has opened is left alone: it fetches fresh when it is. The
 * pages are offset based, so the prepend pushes one row of the next page into
 * this one - which is why `useNotificationsList` de-duplicates by id when it
 * flattens them.
 */
export const prependNotification = (
  queryClient: QueryClient,
  userId: number,
  notification: NotificationEntity
) =>
  updateLists(queryClient, userId, (list) => {
    const known = list.pages.some((page) =>
      page.items.some((item) => item.id === notification.id)
    );
    if (known || list.pages.length === 0) return list;
    const [first, ...rest] = list.pages;
    return {
      ...list,
      pages: [{ ...first, items: [notification, ...first.items] }, ...rest],
    };
  });

/**
 * Mark one as read in place, or every one when `notificationId` is null.
 *
 * In place in the unread list too: the row stays where it is, read, until the
 * list is next fetched, rather than vanishing from under the pointer that
 * just clicked it.
 */
export const markSeenInCache = (
  queryClient: QueryClient,
  userId: number,
  notificationId: number | null
) => {
  const seenAt = new Date().toISOString();
  updateLists(queryClient, userId, (list) => ({
    ...list,
    pages: list.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        !item.seen && (notificationId === null || item.id === notificationId)
          ? { ...item, seen: true, seenAt }
          : item
      ),
    })),
  }));
};

/** Ask the server again - after a gap the socket could not cover. */
export const refreshNotifications = (
  queryClient: QueryClient,
  userId: number
) => {
  queryClient.invalidateQueries({
    queryKey: queryKeys.notifications.unreadCount(userId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.notifications.listAll(userId),
  });
};
